'use client';

import { getFormEditDetailsPayload } from '@/models/forms/forms';
import { useQueryClient } from '@tanstack/react-query';

import {
	createContext,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	useSyncExternalStore,
} from 'react';

import { STATIC_FORM_ID } from '@/features/form-editing/components/constants/form-edit-constants';

import { ISFormEditDetailsResponse } from '@/shared/services/forms/forms.types';

import { getFormSaveQueue } from './form-edit-save-queue';
import { IFormEditPageContext, IFormEditPageProviderProps } from './form-edit-page-provider.types';

export const FormEditPageContext = createContext<IFormEditPageContext>({
	enableKeyboardShortcuts: true,
	handleEnableKeyboardShortcuts: () => {},
	formEditDetails: null,
	handleUpdateFormEditDetails: () => {},
	updateFormEditDetailsStatus: 'idle',
	handleRetryUpdateFormEditDetails: () => {},
	updateFormEditDetailsQueryData: () => {},
	isStaticMode: false,
	isPlaygroundMode: false,
});

export const FormEditPageProvider = ({
	children,
	formEditDetails,
	queryKey,
	isPlaygroundMode = false,
	onUpdateFormEditDetails,
}: IFormEditPageProviderProps) => {
	const [enableKeyboardShortcuts, setEnableKeyboardShortcuts] = useState(true);

	const queryClient = useQueryClient();

	const isStaticMode = useMemo(
		() => formEditDetails?.id === STATIC_FORM_ID || isPlaygroundMode,
		[formEditDetails?.id, isPlaygroundMode]
	);

	const formId = formEditDetails?.id ?? '';

	const saveQueue = useMemo(() => getFormSaveQueue(formId), [formId]);

	const updateFormEditDetailsStatus = useSyncExternalStore(
		useCallback((onChange: () => void) => saveQueue.subscribe(onChange), [saveQueue]),
		saveQueue.getStatus,
		() => 'idle' as const
	);

	// A debounced edit scheduled before the draft switches to its real form can fire after,
	// so the update handler reads the current values rather than the ones it closed over.
	const latestRef = useRef({ queryKey, isStaticMode, isPlaygroundMode, onUpdateFormEditDetails });
	useLayoutEffect(() => {
		latestRef.current = { queryKey, isStaticMode, isPlaygroundMode, onUpdateFormEditDetails };
	});

	const handleEnableKeyboardShortcuts = useCallback((value: boolean) => {
		setEnableKeyboardShortcuts(value);
	}, []);

	useEffect(() => {
		if (updateFormEditDetailsStatus !== 'pending') return;

		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			event.returnValue = '';
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, [updateFormEditDetailsStatus]);

	const handleUpdateFormEditDetails = useCallback<
		IFormEditPageContext['handleUpdateFormEditDetails']
	>(
		(params) => {
			const latest = latestRef.current;
			// Resolve `updater` against the latest cached details so delayed callers
			// (debounced edits) can't overwrite changes that landed after their render.
			const prevFormDetails = queryClient.getQueryData<ISFormEditDetailsResponse>(latest.queryKey);
			if (!prevFormDetails) return;

			const updatedFormEditDetails = params.updater
				? params.updater(prevFormDetails)
				: (params.updatedFormEditDetails ?? {});
			const completeFormDetails = { ...prevFormDetails, ...updatedFormEditDetails };

			queryClient.setQueryData(latest.queryKey, completeFormDetails);

			// In playground mode, call the external update handler
			if (latest.isPlaygroundMode && latest.onUpdateFormEditDetails) {
				latest.onUpdateFormEditDetails(completeFormDetails);
				return;
			}

			// In static mode, only update locally
			if (params.localOnly || latest.isStaticMode) return;

			getFormSaveQueue(completeFormDetails.id).enqueue({
				payload: getFormEditDetailsPayload(completeFormDetails),
				successText: params.successText,
				errorText: params.errorText,
			});
		},
		[queryClient]
	);

	const updateFormEditDetailsQueryData = useCallback(
		(updatedFormEditDetails: Partial<ISFormEditDetailsResponse>) => {
			queryClient.setQueryData(queryKey, (prevData: ISFormEditDetailsResponse) => {
				return {
					...prevData,
					...updatedFormEditDetails,
				};
			});
		},
		[queryClient, queryKey]
	);

	const handleRetryUpdateFormEditDetails = useCallback(() => {
		saveQueue.enqueue({ payload: getFormEditDetailsPayload(formEditDetails) });
	}, [saveQueue, formEditDetails]);

	const value = useMemo(() => {
		return {
			enableKeyboardShortcuts,
			handleEnableKeyboardShortcuts,
			formEditDetails,
			handleUpdateFormEditDetails,
			updateFormEditDetailsStatus,
			handleRetryUpdateFormEditDetails,
			updateFormEditDetailsQueryData,
			isStaticMode,
			isPlaygroundMode,
		};
	}, [
		enableKeyboardShortcuts,
		formEditDetails,
		updateFormEditDetailsStatus,
		handleUpdateFormEditDetails,
		handleEnableKeyboardShortcuts,
		handleRetryUpdateFormEditDetails,
		updateFormEditDetailsQueryData,
		isStaticMode,
		isPlaygroundMode,
	]);

	return <FormEditPageContext.Provider value={value}>{children}</FormEditPageContext.Provider>;
};
